import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Handshake, Pencil, X, RefreshCw, ExternalLink } from "lucide-react";
import api, { apiError } from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  active: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  suspended: "bg-slate-200 text-slate-600",
};

function EditForm({ initial, onSave, onClose }) {
  const [f, setF] = useState({ company_name: initial.company_name || "", website_url: initial.website_url || "",
    logo_url: initial.logo_url || "", partner_page_url: initial.partner_page_url || "",
    industry: initial.industry || "", lifetime_email: initial.lifetime_email || "" });
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/partners/logo", fd);
      setF((prev) => ({ ...prev, logo_url: r.data.url }));
      toast.success("Logo uploaded");
    } catch (e2) { toast.error(apiError(e2)); }
    setUploading(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    try { await onSave(f); onClose(); } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md my-8 space-y-3" data-testid="partner-edit-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Edit partner</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        {[["company_name", "Company name"], ["website_url", "Website URL"],
          ["partner_page_url", "Partner page URL"], ["industry", "Industry"], ["lifetime_email", "Lifetime access email"]].map(([k, label]) => (
          <div key={k}>
            <label className="text-xs font-bold text-slate-500">{label}</label>
            <input className={inputCls} value={f[k]} onChange={set(k)} data-testid={`partner-edit-${k}`} />
          </div>
        ))}
        <div>
          <label className="text-xs font-bold text-slate-500">Logo</label>
          <div className="flex items-center gap-3 mt-1">
            {f.logo_url && <img src={f.logo_url.startsWith("/") ? `${API}${f.logo_url}` : f.logo_url} alt="logo preview" className="h-10 object-contain border border-slate-100 rounded-lg px-2 bg-slate-50" data-testid="partner-edit-logo-preview" />}
            <label className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold px-3.5 py-2 rounded-lg cursor-pointer hover:bg-slate-200 transition-colors">
              {uploading ? "Uploading…" : f.logo_url ? "Replace logo" : "Upload logo"}
              <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={uploadLogo} data-testid="partner-edit-logo-input" />
            </label>
          </div>
          <input className={`${inputCls} mt-2`} placeholder="…or paste a logo URL" value={f.logo_url} onChange={set("logo_url")} data-testid="partner-edit-logo_url" />
        </div>
        <button type="submit" disabled={uploading} className="w-full bg-slate-900 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="partner-edit-save">Save</button>
      </form>
    </div>
  );
}

function AddForm({ onSave, onClose }) {
  const [f, setF] = useState({ company_name: "", website_url: "", industry: "", country: "", lifetime_email: "" });
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/partners/logo", fd);
      setLogoUrl(r.data.url);
    } catch (e2) { toast.error(apiError(e2)); }
    setUploading(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!logoUrl) { toast.error("Upload a logo first"); return; }
    try { await onSave({ ...f, logo_url: logoUrl, lifetime_email: f.lifetime_email || null }); onClose(); }
    catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md my-8 space-y-3" data-testid="partner-add-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Add partner manually</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <input required className={inputCls} placeholder="Company name *" value={f.company_name} onChange={set("company_name")} data-testid="partner-add-company" />
        <input required className={inputCls} placeholder="Website URL (https://…) *" value={f.website_url} onChange={set("website_url")} data-testid="partner-add-website" />
        <input className={inputCls} placeholder="Industry (optional)" value={f.industry} onChange={set("industry")} data-testid="partner-add-industry" />
        <input className={inputCls} placeholder="Country (optional)" value={f.country} onChange={set("country")} data-testid="partner-add-country" />
        <input type="email" className={inputCls} placeholder="Lifetime access email (optional)" value={f.lifetime_email} onChange={set("lifetime_email")} data-testid="partner-add-lifetime" />
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-bold px-4 py-2.5 rounded-xl cursor-pointer hover:bg-emerald-700 transition-colors">
            {uploading ? "Uploading…" : logoUrl ? "Replace logo" : "Upload logo *"}
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={upload} data-testid="partner-add-logo-input" />
          </label>
          {logoUrl && <img src={`${API}${logoUrl}`} alt="logo preview" className="h-10 object-contain border border-slate-100 rounded-lg px-2 bg-slate-50" />}
        </div>
        <button type="submit" className="w-full bg-emerald-700 text-white font-bold py-2.5 rounded-xl hover:bg-emerald-800 transition-colors" data-testid="partner-add-save">
          Add &amp; publish on /partners
        </button>
        <p className="text-[11px] text-slate-400">Goes live immediately as an Active partner. If a lifetime email is provided, Partner Lifetime Access activates automatically.</p>
      </form>
    </div>
  );
}

export default function AdminPartners() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/admin/partners?status=${status}&q=${encodeURIComponent(q)}`);
      setRows(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/partners/${id}/${action}`);
      toast.success(action === "approve" ? "Partner approved — logo published, lifetime access activated, welcome email sent"
        : action === "reject" ? "Application rejected — notification email sent" : `Partner ${action}d`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  const save = async (f) => {
    await api.put(`/admin/partners/${editing.id}`, f);
    toast.success("Partner updated");
    await load();
  };

  return (
    <div data-testid="admin-partners-page">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Handshake size={20} /> Partners</h2>
        <button onClick={() => setAdding(true)} data-testid="partner-add-btn"
          className="bg-emerald-700 text-white text-xs font-extrabold px-4 py-2 rounded-xl hover:bg-emerald-800">+ Add Partner</button>
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {["", "pending", "active", "rejected", "suspended"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`partners-filter-${s || "all"}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border ${status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>
              {s ? s[0].toUpperCase() + s.slice(1) : "All"}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
          <input className={`${inputCls} w-52`} placeholder="Search company, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="partners-search" />
          <button className="bg-slate-900 text-white text-xs font-bold px-4 rounded-xl" data-testid="partners-search-btn">Search</button>
        </form>
      </div>

      {rows.length === 0 && <p className="text-sm text-slate-400 py-10 text-center" data-testid="partners-none">No partner applications found.</p>}

      <div className="space-y-4">
        {rows.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`partner-row-${p.id}`}>
            <div className="flex flex-wrap items-start gap-4">
              {p.logo_url && <img src={p.logo_url.startsWith("/") ? `${API}${p.logo_url}` : p.logo_url} alt="" className="h-12 w-20 object-contain border border-slate-100 rounded-lg bg-slate-50 px-2" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display font-bold">{p.company_name}</h3>
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full uppercase ${STATUS_STYLES[p.status] || ""}`} data-testid={`partner-status-${p.id}`}>{p.status}</span>
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${p.verification_status === "verified" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                    {p.verification_status === "verified" ? "Link verified" : "Link requires review"}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 mt-2 text-xs text-slate-500">
                  <span><strong>Contact:</strong> {p.contact_person} ({p.job_title})</span>
                  <span><strong>Email:</strong> {p.business_email}</span>
                  <span><strong>Industry:</strong> {p.industry} · {p.country}</span>
                  <span><strong>Website:</strong> <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">{p.website_url} <ExternalLink size={10} className="inline" /></a></span>
                  <span><strong>Partner page:</strong> <a href={p.partner_page_url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">{p.partner_page_url} <ExternalLink size={10} className="inline" /></a></span>
                  <span><strong>Applied:</strong> {(p.applied_at || "").slice(0, 10)}</span>
                  <span><strong>Lifetime email:</strong> {p.lifetime_email}</span>
                  <span><strong>Lifetime access:</strong> {p.lifetime_access_status}</span>
                  <span><strong>Terms:</strong> v{p.terms_version} on {(p.terms_accepted_at || "").slice(0, 10)}</span>
                </div>
                {p.description && <p className="text-xs text-slate-400 mt-2 line-clamp-2">{p.description}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {p.status === "pending" && (
                <>
                  <button onClick={() => act(p.id, "approve")} disabled={busyId === p.id} data-testid={`partner-approve-${p.id}`}
                    className="bg-emerald-700 text-white text-xs font-extrabold px-5 py-2 rounded-xl hover:bg-emerald-800 disabled:opacity-50">APPROVE</button>
                  <button onClick={() => act(p.id, "reject", "Reject this application? A notification email will be sent.")} disabled={busyId === p.id} data-testid={`partner-reject-${p.id}`}
                    className="bg-red-600 text-white text-xs font-extrabold px-5 py-2 rounded-xl hover:bg-red-700 disabled:opacity-50">REJECT</button>
                </>
              )}
              {p.status === "active" && (
                <button onClick={() => act(p.id, "suspend", "Suspend this partnership? The logo will be removed from the public page.")} disabled={busyId === p.id} data-testid={`partner-suspend-${p.id}`}
                  className="bg-slate-700 text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-slate-800 disabled:opacity-50">Suspend</button>
              )}
              {(p.status === "suspended" || p.status === "rejected") && (
                <button onClick={() => act(p.id, "reactivate")} disabled={busyId === p.id} data-testid={`partner-reactivate-${p.id}`}
                  className="bg-emerald-700 text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-emerald-800 disabled:opacity-50">Reactivate</button>
              )}
              <button onClick={() => act(p.id, "recheck")} disabled={busyId === p.id} data-testid={`partner-recheck-${p.id}`}
                className="bg-white border border-slate-200 text-slate-600 text-xs font-bold px-4 py-2 rounded-xl hover:border-slate-400 disabled:opacity-50 inline-flex items-center gap-1.5"><RefreshCw size={12} /> Re-check link</button>
              <button onClick={() => setEditing(p)} data-testid={`partner-edit-${p.id}`}
                className="bg-white border border-slate-200 text-slate-600 text-xs font-bold px-4 py-2 rounded-xl hover:border-slate-400 inline-flex items-center gap-1.5"><Pencil size={12} /> Edit</button>
              <button onClick={async () => {
                  if (!window.confirm("Permanently delete this partner record?")) return;
                  try { await api.delete(`/admin/partners/${p.id}`); toast.success("Partner deleted"); await load(); }
                  catch (e2) { toast.error(apiError(e2)); }
                }} data-testid={`partner-delete-${p.id}`}
                className="bg-white border border-red-200 text-red-600 text-xs font-bold px-4 py-2 rounded-xl hover:border-red-400">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <EditForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
      {adding && <AddForm onClose={() => setAdding(false)} onSave={async (f) => {
        await api.post("/admin/partners", f);
        toast.success("Partner added and published");
        await load();
      }} />}
    </div>
  );
}
